import { useEffect, useRef } from "react";

/**
 * Global keyboard-shortcut dispatcher.
 *
 * Supports two flavours:
 *   - Single-key actions:  { "?": openHelp }
 *   - Two-key chords:      { "g d": goDashboard, "n j": newJob }
 *     A chord is a leader key (e.g. "g") followed within 1.2s by a
 *     target key (e.g. "d"). While the leader is "primed" the next
 *     character is the chord match.
 *
 * Behavior:
 *   - Skips when focus is in an input/textarea/contenteditable, OR
 *     when a modifier (Ctrl/Cmd/Alt/Meta) is held (so OS shortcuts
 *     still work).
 *   - Stops propagation + prevents default on match so the browser
 *     doesn't also handle it.
 *
 * Usage:
 *   useKeyboardShortcuts({
 *     "?":   () => setHelpOpen(true),
 *     "g d": () => navigate("/admin/dashboard"),
 *     "g j": () => navigate("/admin/jobs"),
 *   });
 */
export default function useKeyboardShortcuts(shortcuts) {
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  useEffect(() => {
    let leader = null;
    let leaderTimer = null;

    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const onKeyDown = (e) => {
      // Bail when modifier keys are held (browser/OS shortcuts win).
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Bail when the user is typing into a field.
      if (isTypingTarget(e.target)) return;
      // Ignore non-printable / "lone" modifier presses.
      if (e.key.length !== 1 && e.key !== "?") return;

      const map = shortcutsRef.current || {};
      // Resolve a chord (leader + this key).
      if (leader) {
        const chord = `${leader} ${e.key}`;
        clearTimeout(leaderTimer);
        leader = null;
        if (typeof map[chord] === "function") {
          e.preventDefault();
          e.stopPropagation();
          map[chord](e);
        }
        return;
      }
      // Single-key match (no leader).
      if (typeof map[e.key] === "function") {
        e.preventDefault();
        e.stopPropagation();
        map[e.key](e);
        return;
      }
      // Maybe this key is a leader for a chord.
      const isLeader = Object.keys(map).some((k) => k.startsWith(`${e.key} `));
      if (isLeader) {
        leader = e.key;
        leaderTimer = setTimeout(() => { leader = null; }, 1200);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (leaderTimer) clearTimeout(leaderTimer);
    };
  }, []);
}
